import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext.tsx';
import type { InviteEmailResult } from '../../hooks/useEndConsumers.ts';
import { getApiUrl } from '../../config/api.ts';
import { createLogger, toErrorMessage } from '@metron/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.tsx';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { Checkbox } from '../ui/checkbox.tsx';
import { Alert, AlertDescription } from '../ui/alert.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.tsx';
import { AlertCircle, CheckCircle2, FileText, Upload, XCircle } from 'lucide-react';

const logger = createLogger('ImportEndConsumerInvitesModal');

interface ValidRow {
  rowNumber: number;
  deviceId: string;
  email: string;
  name: string;
  sensorId: number;
}
interface ExistingRow {
  rowNumber: number;
  deviceId: string;
  email: string;
}
interface InvalidRow {
  rowNumber: number;
  deviceId: string;
  email: string;
  reason: string;
}
interface FormatError {
  rowNumber: number;
  message: string;
}
interface Preview {
  formatErrors: FormatError[];
  valid: ValidRow[];
  existing: ExistingRow[];
  invalid: InvalidRow[];
  duplicateCount: number;
}
interface CommitResult {
  summary: {
    consumers: number;
    groupsCreated: number;
    metersAdded: number;
    invitesCreated: number;
  };
  existing: ExistingRow[];
  invalid: InvalidRow[];
  formatErrors: FormatError[];
  // Present only when sendInviteEmail was requested.
  inviteEmails?: InviteEmailResult[];
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: number;
  onComplete: () => void;
}

export function ImportEndConsumerInvitesModal(
  { open, onOpenChange, customerId, onComplete }: Props,
) {
  const { getAccessToken } = useAuth();
  const [csvContent, setCsvContent] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [result, setResult] = useState<CommitResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [sendInviteEmail, setSendInviteEmail] = useState(false);

  const post = useCallback(async <T,>(
    path: string,
    content: string,
    extra: { sendInviteEmail?: boolean } = {},
  ): Promise<T> => {
    const token = await getAccessToken();
    const res = await fetch(getApiUrl(path), {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerID: customerId, csvContent: content, ...extra }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(body.error ?? `Request failed (${res.status})`);
    }
    return res.json() as Promise<T>;
  }, [getAccessToken, customerId]);

  const handleFile = useCallback((file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast.error('Please choose a .csv file');
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const content = String(reader.result);
      setCsvContent(content);
      setFileName(file.name);
      setResult(null);
      setIsLoading(true);
      try {
        setPreview(await post<Preview>('end-consumer-invites/validate', content));
      } catch (e) {
        logger.error({ err: toErrorMessage(e) }, 'validate failed');
        toast.error(toErrorMessage(e));
      } finally {
        setIsLoading(false);
      }
    };
    reader.readAsText(file);
  }, [post]);

  const handleCommit = useCallback(async () => {
    if (!csvContent) return;
    setIsLoading(true);
    try {
      const r = await post<CommitResult>('end-consumer-invites', csvContent, { sendInviteEmail });
      setResult(r);
      toast.success(
        `Created ${r.summary.invitesCreated} invite(s) for ${r.summary.consumers} consumer(s)`,
      );
      const failedEmails = r.inviteEmails?.filter((e) => !e.sent).length ?? 0;
      if (failedEmails > 0) {
        toast.warning(`${failedEmails} onboarding email(s) failed to send`);
      }
      onComplete();
    } catch (e) {
      toast.error(toErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }, [csvContent, post, onComplete, sendInviteEmail]);

  const handleClose = useCallback(() => {
    setCsvContent(null);
    setFileName('');
    setPreview(null);
    setSendInviteEmail(false);
    setResult(null);
    setIsLoading(false);
    setIsDragging(false);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFile(dropped);
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const totalIssues = preview ? preview.formatErrors.length + preview.invalid.length : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[700px] max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Import Consumer Invites from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV with columns <code>device_id</code>, <code>email</code>, and an optional
            {' '}
            <code>name</code>. The server will validate each row against this customer's meters and
            show a preview before committing.
          </DialogDescription>
        </DialogHeader>

        {!result
          ? (
            <div className='grid gap-4 py-4'>
              {/* CSV format help */}
              <div className='rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground space-y-2'>
                <p className='font-medium text-foreground'>Expected CSV format</p>
                <p>A header row, then one row per meter. Columns:</p>
                <ul className='list-disc pl-4 space-y-0.5'>
                  <li>
                    <code>device_id</code>{' '}
                    — the meter's device ID (must belong to this customer; matched exactly,
                    case-sensitive)
                  </li>
                  <li>
                    <code>email</code> — the consumer's email address
                  </li>
                  <li>
                    <code>name</code>{' '}
                    — optional consumer display name. Used to pre-fill their account; if omitted
                    they can set it themselves at sign-up.
                  </li>
                </ul>
                <p>
                  A consumer with multiple meters gets one row per meter (repeat the email); they're
                  grouped into a single account:
                </p>
                <pre className='rounded border bg-background p-2 font-mono leading-relaxed overflow-x-auto'>
device_id,email,name
ABC123,jane@example.com,Jane Doe
DEF456,jane@example.com,Jane Doe
GHI789,john@example.com,</pre>
              </div>

              {/* File Upload Area */}
              <div className='grid gap-2'>
                <Label htmlFor='ec-csv-file'>CSV File</Label>
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    isDragging
                      ? 'border-primary bg-primary/5'
                      : 'border-muted-foreground/25 hover:border-muted-foreground/50'
                  }`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  {csvContent
                    ? (
                      <div className='flex flex-col items-center gap-2'>
                        <FileText className='h-12 w-12 text-muted-foreground' />
                        <p className='text-sm font-medium'>{fileName}</p>
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          onClick={() => {
                            setCsvContent(null);
                            setFileName('');
                            setPreview(null);
                          }}
                          disabled={isLoading}
                        >
                          Remove
                        </Button>
                      </div>
                    )
                    : (
                      <>
                        <Upload className='h-12 w-12 mx-auto mb-4 text-muted-foreground' />
                        <p className='text-sm font-medium mb-2'>
                          Drag and drop CSV file here, or click to select
                        </p>
                        <Input
                          id='ec-csv-file'
                          type='file'
                          accept='.csv'
                          className='max-w-xs mx-auto'
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleFile(f);
                          }}
                          disabled={isLoading}
                        />
                      </>
                    )}
                </div>
              </div>

              {/* Loading indicator */}
              {isLoading && (
                <p className='text-sm text-muted-foreground text-center'>
                  Validating…
                </p>
              )}

              {/* Preview tables */}
              {preview && !isLoading && (
                <div className='space-y-4'>
                  {/* Valid rows */}
                  <div>
                    <div className='flex items-center gap-2 mb-2'>
                      <CheckCircle2 className='h-4 w-4 text-green-600' />
                      <span className='text-sm font-medium'>
                        Valid — {preview.valid.length} row{preview.valid.length !== 1 ? 's' : ''}
                        {' '}
                        will be committed
                      </span>
                      {preview.duplicateCount > 0 && (
                        <span className='text-xs text-muted-foreground'>
                          ({preview.duplicateCount}{' '}
                          duplicate{preview.duplicateCount !== 1 ? 's' : ''} skipped)
                        </span>
                      )}
                    </div>
                    {preview.valid.length > 0 && (
                      <div className='rounded-md border max-h-48 overflow-y-auto'>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Row</TableHead>
                              <TableHead>Device ID</TableHead>
                              <TableHead>Email</TableHead>
                              <TableHead>Name</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {preview.valid.map((row) => (
                              <TableRow key={`${row.rowNumber}-${row.deviceId}`}>
                                <TableCell>{row.rowNumber}</TableCell>
                                <TableCell className='font-mono text-xs'>{row.deviceId}</TableCell>
                                <TableCell>{row.email}</TableCell>
                                <TableCell className='text-muted-foreground'>
                                  {row.name || '—'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>

                  {/* Already-invited rows */}
                  {preview.existing.length > 0 && (
                    <div>
                      <div className='flex items-center gap-2 mb-2'>
                        <AlertCircle className='h-4 w-4 text-yellow-600' />
                        <span className='text-sm font-medium'>
                          Already invited — {preview.existing.length}{' '}
                          row{preview.existing.length !== 1 ? 's' : ''} (no-op)
                        </span>
                      </div>
                      <div className='rounded-md border max-h-36 overflow-y-auto'>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Row</TableHead>
                              <TableHead>Device ID</TableHead>
                              <TableHead>Email</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {preview.existing.map((row) => (
                              <TableRow key={`${row.rowNumber}-${row.deviceId}`}>
                                <TableCell>{row.rowNumber}</TableCell>
                                <TableCell className='font-mono text-xs'>{row.deviceId}</TableCell>
                                <TableCell>{row.email}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}

                  {/* Invalid + format-error rows */}
                  {totalIssues > 0 && (
                    <div>
                      <div className='flex items-center gap-2 mb-2'>
                        <XCircle className='h-4 w-4 text-destructive' />
                        <span className='text-sm font-medium'>
                          Invalid — {totalIssues} row{totalIssues !== 1 ? 's' : ''} will be skipped
                        </span>
                      </div>
                      <Alert variant='destructive'>
                        <AlertCircle className='h-4 w-4' />
                        <AlertDescription>
                          <div className='space-y-1 text-xs max-h-36 overflow-y-auto'>
                            {preview.invalid.map((row) => (
                              <div key={`inv-${row.rowNumber}-${row.deviceId}`}>
                                <span className='font-semibold'>Row {row.rowNumber}:</span>{' '}
                                {row.reason}
                              </div>
                            ))}
                            {preview.formatErrors.map((err) => (
                              <div key={`fmt-${err.rowNumber}`}>
                                <span className='font-semibold'>Row {err.rowNumber}:</span>{' '}
                                {err.message}
                              </div>
                            ))}
                          </div>
                        </AlertDescription>
                      </Alert>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
          : (
            /* Commit result summary */
            <div className='grid gap-4 py-4'>
              <div className='space-y-3'>
                <Alert>
                  <CheckCircle2 className='h-4 w-4' />
                  <AlertDescription>
                    <div className='font-medium mb-1'>Import complete</div>
                    <ul className='text-sm space-y-0.5'>
                      <li>Consumers: {result.summary.consumers}</li>
                      <li>Groups created: {result.summary.groupsCreated}</li>
                      <li>Meters added: {result.summary.metersAdded}</li>
                      <li>Invites created: {result.summary.invitesCreated}</li>
                      {result.inviteEmails && (
                        <li>
                          Onboarding emails sent: {result.inviteEmails.filter((e) => e.sent).length}
                          {' '}
                          of {result.inviteEmails.length}
                        </li>
                      )}
                    </ul>
                  </AlertDescription>
                </Alert>
                {result.inviteEmails?.some((e) => !e.sent) && (
                  <Alert variant='destructive'>
                    <AlertCircle className='h-4 w-4' />
                    <AlertDescription>
                      <div className='font-medium mb-1'>
                        Onboarding email failed for{' '}
                        {result.inviteEmails.filter((e) => !e.sent).length} consumer(s)
                      </div>
                      <div className='text-xs space-y-0.5 max-h-32 overflow-y-auto'>
                        {result.inviteEmails.filter((e) => !e.sent).map((e) => (
                          <div key={`email-${e.email}`}>
                            <span className='font-semibold'>{e.email}:</span> {e.error}
                          </div>
                        ))}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
                {(result.existing.length > 0 || result.invalid.length > 0 ||
                  result.formatErrors.length > 0) && (
                  <Alert variant='destructive'>
                    <AlertCircle className='h-4 w-4' />
                    <AlertDescription>
                      <div className='font-medium mb-1'>
                        {result.existing.length + result.invalid.length +
                          result.formatErrors.length} row(s) skipped
                      </div>
                      <div className='text-xs space-y-0.5 max-h-32 overflow-y-auto'>
                        {result.existing.map((row) => (
                          <div key={`ex-${row.rowNumber}`}>
                            <span className='font-semibold'>Row {row.rowNumber}:</span>{' '}
                            already invited
                          </div>
                        ))}
                        {result.invalid.map((row) => (
                          <div key={`inv-${row.rowNumber}`}>
                            <span className='font-semibold'>Row {row.rowNumber}:</span> {row.reason}
                          </div>
                        ))}
                        {result.formatErrors.map((err) => (
                          <div key={`fmt-${err.rowNumber}`}>
                            <span className='font-semibold'>Row {err.rowNumber}:</span>{' '}
                            {err.message}
                          </div>
                        ))}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </div>
          )}

        {!result && preview && preview.valid.length > 0 && (
          <div className='flex items-start gap-2'>
            <Checkbox
              id='import-send-invite'
              checked={sendInviteEmail}
              onCheckedChange={(checked) => setSendInviteEmail(checked === true)}
              disabled={isLoading}
            />
            <div className='grid gap-1'>
              <Label htmlFor='import-send-invite'>
                Send onboarding emails to new consumers
              </Label>
              <p className='text-sm text-muted-foreground'>
                Emails each new consumer a link to sign up for MyWaterScope. Consumers who already
                exist aren't emailed.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type='button' variant='outline' onClick={handleClose} disabled={isLoading}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && preview && (
            <Button
              type='button'
              onClick={handleCommit}
              disabled={isLoading || preview.valid.length === 0}
            >
              {isLoading
                ? 'Committing…'
                : `Commit ${preview.valid.length} invite${preview.valid.length !== 1 ? 's' : ''}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
